/**
 * Coups de pouce, Attendus 1242 « utiliser la propriété du triangle
 * rectangle et de son cercle circonscrit », 1243 « écrire l'égalité de
 * Pythagore dans un triangle rectangle », 1244 « utiliser les théorèmes de
 * la droite des milieux » (3e, Géométrie plane). Seconde passe 3e
 * (2026-09-13). UNE page STATIQUE par attendu, le même triangle ABC
 * rectangle en A (les lettres A, B, C sont sacrifiées par 1243 et 1244).
 *
 * L'image que l'élève a déjà : le triangle rectangle de 4e avec son angle
 * droit marqué, l'hypoténuse en face (fiche 4e « le côté en face de
 * l'angle droit »), le cercle de 4e (l. 956 : le cercle circonscrit du
 * triangle rectangle), la droite des milieux de 4e.
 *   1242 « L'hypoténuse est un diamètre » : ABC rectangle en A, BC = 10 ;
 *     le cercle passe par A, B, C ; son centre O est le milieu de [BC] ;
 *     rayon = 10 ÷ 2 = 5. Dans l'autre sens : si un côté est un diamètre
 *     du cercle qui passe par les trois sommets, le triangle est rectangle
 *     (angle droit en face du diamètre).
 *   1243 « L'hypoténuse seule d'un côté du = » : BC² = AB² + AC², le côté
 *     en face de l'angle droit, le plus long, seul à gauche.
 *   1244 « La moitié » : M milieu de [AB], N milieu de [AC] : (MN) est
 *     parallèle à (BC) et MN = BC ÷ 2 = 8 ÷ 2 = 4 ; dans l'autre sens, la
 *     parallèle à (BC) qui passe par le milieu M coupe [AC] en son milieu.
 *
 * SACRIFIÉS : (hypoténuse 10 ; rayon 5) en 1242, le triple {A, B, C} en
 * 1243 et 1244, (8 ; 4) en 1244 (gen1242, gen1243-1244 self-tests).
 */
import { svg } from './1054';

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#64748B';
const BLEU_CLAIR = '#DBEAFE';

const ligne = (y, contenu, taille = 12, couleur = ENCRE) => `<text x="200" y="${y}" font-size="${taille}" text-anchor="middle" fill="${couleur}">${contenu}</text>`;
const gras = (t, couleur = ENCRE) => `<tspan font-weight="bold" fill="${couleur}">${t}</tspan>`;
const point = (p, nom, dx, dy) => `<text x="${p.x + dx}" y="${p.y + dy}" font-size="14" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${nom}</text>`;
const seg = (P, Q, couleur = ENCRE, ep = 1.6, extra = '') => `<line x1="${P.x}" y1="${P.y}" x2="${Q.x}" y2="${Q.y}" stroke="${couleur}" stroke-width="${ep}" ${extra}/>`;
/** L'angle droit en A, entre les directions de B et de C. */
const angleDroit = (A, B, C, t = 12) => {
    const u = { x: B.x - A.x, y: B.y - A.y }; const v = { x: C.x - A.x, y: C.y - A.y };
    const nu = Math.hypot(u.x, u.y); const nv = Math.hypot(v.x, v.y);
    const p1 = { x: A.x + (u.x / nu) * t, y: A.y + (u.y / nu) * t };
    const p2 = { x: A.x + (v.x / nv) * t, y: A.y + (v.y / nv) * t };
    const p3 = { x: p1.x + p2.x - A.x, y: p1.y + p2.y - A.y };
    return `<polyline points="${p1.x},${p1.y} ${p3.x},${p3.y} ${p2.x},${p2.y}" fill="none" stroke="${ORANGE}" stroke-width="1.4"/>`;
};

// ─── 1242 : le cercle, [BC] diamètre ───────────────────────────────────────
// Cercle de centre O(120, 110), rayon 70 ; B et C aux extrémités d'un diamètre, A sur le cercle.
const O = { x: 120, y: 112 }; const R = 70;
const B2 = { x: O.x - R, y: O.y }; const C2 = { x: O.x + R, y: O.y };
const A2 = { x: O.x + R * Math.cos(-2.1), y: O.y + R * Math.sin(-2.1) };

const SVG_CERCLE = svg(
    'Un triangle A B C rectangle en A, inscrit dans un cercle. L’hypoténuse B C est un diamètre du cercle : le centre O est le milieu de B C. B C égale 10 centimètres, donc le rayon égale 10 divisé par 2 égale 5 centimètres. Dans l’autre sens : si un côté est un diamètre du cercle qui passe par les trois sommets, le triangle est rectangle, l’angle droit est en face.',
    `<circle cx="${O.x}" cy="${O.y}" r="${R}" fill="${BLEU_CLAIR}" stroke="${BLEU}" stroke-width="1.6"/>
  ${seg(B2, C2, BLEU, 2.4)}
  ${seg(A2, B2)} ${seg(A2, C2)}
  ${angleDroit(A2, B2, C2)}
  <circle cx="${O.x}" cy="${O.y}" r="3" fill="${BLEU}"/>
  ${point(A2, 'A', -4, -8)} ${point(B2, 'B', -12, 5)} ${point(C2, 'C', 12, 5)}
  <text x="${O.x}" y="${O.y + 16}" font-size="12" font-weight="bold" text-anchor="middle" fill="${BLEU}">O</text>
  <text x="${O.x + 40}" y="${O.y + 16}" font-size="11" font-weight="bold" text-anchor="middle" fill="${BLEU}">BC = 10</text>
  <text x="250" y="70" font-size="12" fill="${ENCRE}">rectangle en <tspan fill="${ORANGE}" font-weight="bold">A</tspan> :</text>
  <text x="250" y="88" font-size="12" fill="${BLEU}">l’hypoténuse [BC] est</text>
  <text x="250" y="104" font-size="12" fill="${BLEU}">un <tspan font-weight="bold">diamètre</tspan> du cercle</text>
  <text x="250" y="130" font-size="12" fill="${ENCRE}">O est le milieu de [BC]</text>
  <text x="250" y="148" font-size="12" fill="${ENCRE}">rayon = 10 ÷ 2 = <tspan font-weight="bold">5</tspan></text>
  ${ligne(216, `Triangle rectangle : l’hypoténuse est un ${gras('diamètre', BLEU)}, son milieu est le centre.`, 12)}
  ${ligne(238, `Dans l’autre sens : un côté est un diamètre, le triangle est ${gras('rectangle', ORANGE)},`, 12)}
  ${ligne(256, 'l’angle droit est en face de ce diamètre.', 12, GRIS)}`,
    272,
);

// ─── 1243 : BC² = AB² + AC² ────────────────────────────────────────────────
const A3 = { x: 70, y: 150 }; const B3 = { x: 220, y: 150 }; const C3 = { x: 70, y: 50 };

const SVG_PYTHAGORE = svg(
    'Un triangle A B C rectangle en A. L’hypoténuse B C, en orange, est le côté en face de l’angle droit, le plus long. L’égalité de Pythagore : B C au carré égale A B au carré plus A C au carré. L’hypoténuse est seule d’un côté du égal.',
    `${seg(A3, B3, BLEU, 1.8)} ${seg(A3, C3, BLEU, 1.8)} ${seg(B3, C3, ORANGE, 2.4)}
  ${angleDroit(A3, B3, C3)}
  ${point(A3, 'A', -10, 12)} ${point(B3, 'B', 12, 12)} ${point(C3, 'C', -10, -6)}
  <text x="${(B3.x + C3.x) / 2 + 34}" y="${(B3.y + C3.y) / 2 - 6}" font-size="11" font-weight="bold" fill="${ORANGE}">hypoténuse</text>
  <text x="${(B3.x + C3.x) / 2 + 34}" y="${(B3.y + C3.y) / 2 + 8}" font-size="11" fill="${ORANGE}">en face de l’angle droit</text>
  <text x="${(B3.x + C3.x) / 2 + 34}" y="${(B3.y + C3.y) / 2 + 22}" font-size="11" fill="${GRIS}">le côté le plus long</text>
  ${ligne(196, `${gras('BC²', ORANGE)} = ${gras('AB²', BLEU)} + ${gras('AC²', BLEU)}`, 22)}
  ${ligne(224, `L’${gras('hypoténuse', ORANGE)} est seule d’un côté du symbole « = »,`, 12)}
  ${ligne(242, 'les deux autres côtés de l’autre.', 12)}
  ${ligne(264, 'Je cherche d’abord l’angle droit : l’hypoténuse est en face.', 12, GRIS)}`,
    280,
);

// ─── 1244 : la droite des milieux ──────────────────────────────────────────
const A4 = { x: 90, y: 40 }; const B4 = { x: 30, y: 160 }; const C4 = { x: 210, y: 160 };
const M4 = { x: (A4.x + B4.x) / 2, y: (A4.y + B4.y) / 2 }; const N4 = { x: (A4.x + C4.x) / 2, y: (A4.y + C4.y) / 2 };
const tick = (P, Q, couleur) => {
    const mx = (P.x + Q.x) / 2; const my = (P.y + Q.y) / 2; const dx = Q.x - P.x; const dy = Q.y - P.y; const n = Math.hypot(dx, dy);
    const px = (-dy / n) * 5; const py = (dx / n) * 5;
    return `<line x1="${mx + px}" y1="${my + py}" x2="${mx - px}" y2="${my - py}" stroke="${couleur}" stroke-width="1.6"/>`;
};

const SVG_MILIEUX = svg(
    'Un triangle A B C. M est le milieu de A B, N est le milieu de A C. La droite M N, en orange, est parallèle à B C, en bleu, et M N égale la moitié de B C : B C égale 8, donc M N égale 4. Dans l’autre sens : la parallèle à B C qui passe par le milieu M coupe A C en son milieu.',
    `${seg(A4, B4)} ${seg(A4, C4)} ${seg(B4, C4, BLEU, 2.4)}
  ${seg(M4, N4, ORANGE, 2.4)}
  ${tick(A4, M4, GRIS)} ${tick(M4, B4, GRIS)}
  ${tick(A4, N4, GRIS)} ${tick(N4, C4, GRIS)}
  <circle cx="${M4.x}" cy="${M4.y}" r="3" fill="${ORANGE}"/> <circle cx="${N4.x}" cy="${N4.y}" r="3" fill="${ORANGE}"/>
  ${point(A4, 'A', 0, -8)} ${point(B4, 'B', -10, 12)} ${point(C4, 'C', 10, 12)}
  ${point(M4, 'M', -12, 4)} ${point(N4, 'N', 12, 4)}
  <text x="${(B4.x + C4.x) / 2}" y="${B4.y + 16}" font-size="12" font-weight="bold" text-anchor="middle" fill="${BLEU}">BC = 8</text>
  <text x="${(M4.x + N4.x) / 2}" y="${M4.y - 8}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ORANGE}">MN = 4</text>
  <text x="240" y="70" font-size="12" fill="${ENCRE}">M et N : les <tspan font-weight="bold">milieux</tspan></text>
  <text x="240" y="86" font-size="12" fill="${ENCRE}">de deux côtés, donc :</text>
  <text x="240" y="110" font-size="12" fill="${ORANGE}">(MN) est <tspan font-weight="bold">parallèle</tspan> à (BC)</text>
  <text x="240" y="134" font-size="12" fill="${ORANGE}">MN = BC ÷ 2 = 8 ÷ 2 = <tspan font-weight="bold">4</tspan></text>
  ${ligne(200, 'Deux milieux : parallèle au troisième côté, et la moitié de sa longueur.', 12)}
  ${ligne(222, `Dans l’autre sens : la parallèle à (BC) qui passe par le milieu ${gras('M')}`, 12)}
  ${ligne(240, 'coupe [AC] en son milieu N.', 12, GRIS)}`,
    256,
);

const AIDE_CERCLE = { pages: [{ titre: 'L’hypoténuse est un diamètre', svg: SVG_CERCLE }] };
const AIDE_PYTHAGORE = { pages: [{ titre: 'L’hypoténuse seule d’un côté du =', svg: SVG_PYTHAGORE }] };
const AIDE_MILIEUX = { pages: [{ titre: 'Deux milieux : parallèle et moitié', svg: SVG_MILIEUX }] };

const CP_1242 = {
    reciproque_qcm_unique: AIDE_CERCLE,
    diametre_qcm_unique: AIDE_CERCLE,
    rayon_texte_trou_saisie: AIDE_CERCLE,
    rectangle_qcm_unique: AIDE_CERCLE,
    proprietes_tableau_vf: AIDE_CERCLE,
};
export const CP_1243 = {
    egalite_qcm_unique: AIDE_PYTHAGORE,
    egalite_saisie: AIDE_PYTHAGORE,
    sens_tableau_vf: AIDE_PYTHAGORE,
};
export const CP_1244 = {
    parallele_qcm_unique: AIDE_MILIEUX,
    longueur_flash_texte_trou_saisie: AIDE_MILIEUX,
    milieu_qcm_unique: AIDE_MILIEUX,
    sens_tableau_vf: AIDE_MILIEUX,
    redaction_texte_trou_drag: AIDE_MILIEUX,
};

export default CP_1242;
